import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Role, Permission } from '../models';

interface CreateRoleRequest {
  name: string;
  description?: string;
  permissionCodes: string[];
}

interface UpdateRoleRequest {
  name: string;
  description?: string;
  permissionCodes: string[];
}

interface RoleResponse {
  id: number;
  name: string;
  description?: string;
  permissions: string[];
  usersCount: number;
  createdAt: string;
  updatedAt: string;
}

interface PermissionResponse {
  id: number;
  code: string;
  name: string;
  description?: string;
  module?: string;
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class RolesService {
  private apiUrl = '/api/roles';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    return this.authService.getAuthHeaders();
  }

  getAllRoles(): Observable<Role[]> {
    return this.http.get<RoleResponse[]>(this.apiUrl, {
      headers: this.getHeaders()
    }).pipe(
      map(roles => roles.map(r => this.mapToRole(r)))
    );
  }

  getRoleById(id: number): Observable<Role> {
    return this.http.get<RoleResponse>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      map(role => this.mapToRole(role))
    );
  }

  getAllPermissions(): Observable<Permission[]> {
    return this.http.get<PermissionResponse[]>(`${this.apiUrl}/permissions`, {
      headers: this.getHeaders()
    }).pipe(
      map(permissions => permissions.map(p => this.mapToPermission(p)))
    );
  }

  createRole(request: CreateRoleRequest): Observable<Role> {
    return this.http.post<RoleResponse>(this.apiUrl, request, {
      headers: this.getHeaders()
    }).pipe(
      map(role => this.mapToRole(role))
    );
  }

  updateRole(id: number, request: UpdateRoleRequest): Observable<Role> {
    return this.http.put<RoleResponse>(`${this.apiUrl}/${id}`, request, {
      headers: this.getHeaders()
    }).pipe(
      map(role => this.mapToRole(role))
    );
  }

  deleteRole(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    });
  }

  private mapToRole(response: RoleResponse): Role {
    return {
      id: response.id,
      name: response.name,
      description: response.description,
      permissions: response.permissions,
      usersCount: response.usersCount,
      createdAt: new Date(response.createdAt)
    };
  }

  private mapToPermission(response: PermissionResponse): Permission {
    return {
      id: response.id,
      code: response.code,
      name: response.name,
      description: response.description,
      module: response.module,
      createdAt: new Date(response.createdAt)
    };
  }
}